// pages/TalleresHistorialCiclos.jsx
import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Drawer, Spin, Empty, Alert, Tag,
    Space, Tabs, Tooltip, App
} from 'antd';
import {
    ArrowLeftOutlined, EyeOutlined, DownloadOutlined,
    CalendarOutlined, FileExcelOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import CiclosService from '../../../../../service/Ciclos';
import useAuthContext from '../../../../../hooks/useAuthContext';
import { exportarLibro } from '../../../../../utils/exportExcel';
import { LoadingOutlined } from '@ant-design/icons';
import { FiArchive, FiCheck, FiClock, FiDollarSign, FiDownload, FiFileText, FiInfo, FiUsers, FiUserX } from 'react-icons/fi';
import { claseBtnPrimario, claseBtnSecundario, ChipNeutro } from '../../../../Ui/Admin/PiezasAdmin';
import { CabeceraModal, ChipCabecera, Chip, Kpi, Etiqueta, Vacio, claseTituloSeccion } from '../piezasTalleres';

// Cómo se cerró el ciclo, en palabras de la administración.
const MOTIVOS_CIERRE = {
    cierre_manual: 'Cierre manual',
    fecha_fin_alcanzada: 'Terminó el período',
    inicio_nuevo_ciclo: 'Se inició un nuevo ciclo',
    otro: 'Otro motivo',
};

const ESTADOS_INSCRIPCION = {
    activa: ['azul', 'Activa'],
    confirmada: ['azul', 'Confirmada'],
    pendiente: ['amarillo', 'Pendiente'],
    cancelada: ['rojo', 'Cancelada'],
};


const TalleresHistorialCiclos = () => {
    const { tallerId } = useParams();
    const navigate = useNavigate();
    const { modal } = App.useApp();
    const { user } = useAuthContext();

    // Igual que la ficha de usuario: en pantallas angostas el detalle ocupa todo el ancho.
    const angosto = typeof window !== 'undefined' && window.innerWidth < 900;

    // Estados
    const [taller, setTaller] = useState(null);
    const [ciclos, setCiclos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [cicloSeleccionado, setCicloSeleccionado] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [estadisticasGenerales, setEstadisticasGenerales] = useState(null);
    const [descargandoExcel, setDescargandoExcel] = useState(false);

    // Cargar datos
    useEffect(() => {
        cargarHistorialCiclos();
        cargarEstadisticas();
    }, [tallerId, pagination.current, pagination.pageSize]);

    const cargarHistorialCiclos = async () => {
        setLoading(true);
        try {
            const resultado = await CiclosService.ObtenerHistorialCiclos(tallerId, {
                page: pagination.current,
                limit: pagination.pageSize
            });

            if (resultado.ciclos.length > 0) {
                setTaller(resultado.ciclos[0].datosTaller);
            }

            setCiclos(resultado.ciclos);
            setPagination(prev => ({
                ...prev,
                total: resultado.total
            }));
        } catch (error) {
            modal.error({
                title: 'Error al cargar historial',
                content: error?.error || 'No se pudo cargar el historial de ciclos'
            });
        } finally {
            setLoading(false);
        }
    };

    const cargarEstadisticas = async () => {
        try {
            const resultado = await CiclosService.ObtenerEstadisticas();
            setEstadisticasGenerales(resultado.data);
        } catch (error) {
            console.error('Error cargando estadísticas:', error);
        }
    };

    // Ver detalle del ciclo
    const handleVerDetalle = async (ciclo) => {
        try {
            const detalleCompleto = await CiclosService.ObtenerDetalleCiclo(ciclo._id);
            setCicloSeleccionado(detalleCompleto);
            setDrawerVisible(true);
        } catch (error) {
            modal.error({
                title: 'Error al cargar detalle',
                content: error?.error || 'No se pudo cargar el detalle del ciclo'
            });
        }
    };

    // Descargar ciclo como JSON
    const handleDescargarCiclo = (ciclo) => {
        const dataStr = JSON.stringify(ciclo, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ciclo-${ciclo.numeroCiclo}-${ciclo.datosTaller.nombre}.json`;
        link.click();
        URL.revokeObjectURL(url);
    };

    // Descargar inscritos como Excel
    const handleDescargarInscritos = async (ciclo) => {
        let cicloConDetalle = ciclo;

        // Si viene de la tabla sin inscripcionesSnapshot, obtener el detalle primero
        if (!ciclo.inscripcionesSnapshot) {
            try {
                setDescargandoExcel(true);
                cicloConDetalle = await CiclosService.ObtenerDetalleCiclo(ciclo._id);
            } catch (error) {
                modal.error({
                    title: 'Error al obtener inscritos',
                    content: error?.error || 'No se pudo cargar el detalle del ciclo',
                });
                setDescargandoExcel(false);
                return;
            }
        }

        const inscritos = cicloConDetalle.inscripcionesSnapshot || [];

        if (inscritos.length === 0) {
            setDescargandoExcel(false);
            modal.warning({
                title: 'Sin inscritos',
                content: 'Este ciclo no tiene inscripciones registradas.',
            });
            return;
        }

        try {
            const nombreTaller = cicloConDetalle.datosTaller?.nombre || 'taller';
            const confirmadas = inscritos.filter((i) => i.estado === 'confirmada').length;
            // La inscripción solo existe con el pago aprobado: `confirmada` es pagada. El
            // respaldo por `estado` cubre una API que todavía no manda `pagoCompletado`.
            const estaPagada = (i) => (typeof i.pagoCompletado === 'boolean' ? i.pagoCompletado : i.estado === 'confirmada');
            const textoPago = (i) => {
                if (!estaPagada(i)) return 'No';
                return typeof i.montoTotal === 'number' && i.montoTotal > 0 ? 'Sí' : 'Gratuito';
            };
            const pagadas = inscritos.filter(estaPagada).length;
            const recaudado = inscritos.reduce(
                (suma, i) => suma + (typeof i.montoTotal === 'number' ? i.montoTotal : 0), 0
            );

            await exportarLibro({
                archivo: `inscritos-ciclo${cicloConDetalle.numeroCiclo}-${nombreTaller.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
                titulo: `Inscritos · Ciclo ${cicloConDetalle.numeroCiclo}`,
                subtitulo: nombreTaller,
                usuario: user,
                filtros: {
                    'Taller': nombreTaller,
                    'Recinto': cicloConDetalle.datosTaller?.recinto?.nombre || cicloConDetalle.datosTaller?.recintoNombre || null,
                    'Ciclo': `N.º ${cicloConDetalle.numeroCiclo}`,
                    'Inicio del ciclo': cicloConDetalle.fechaInicio ? dayjs(cicloConDetalle.fechaInicio).format('DD/MM/YYYY') : null,
                    'Cierre del ciclo': cicloConDetalle.fechaCierre ? dayjs(cicloConDetalle.fechaCierre).format('DD/MM/YYYY') : null,
                },
                hojas: [
                    {
                        nombre: 'Resumen',
                        metricas: [
                            { metrica: 'Inscripciones del ciclo' },
                            { metrica: 'Total de inscripciones', valor: inscritos.length, tipo: 'numero' },
                            { metrica: 'Confirmadas', valor: confirmadas, tipo: 'numero' },
                            { metrica: 'Con pago completado', valor: pagadas, tipo: 'numero' },
                            { metrica: 'Recaudación' },
                            { metrica: 'Monto total del ciclo', valor: recaudado, tipo: 'moneda' },
                            {
                                metrica: 'Ticket promedio',
                                valor: inscritos.length ? Math.round(recaudado / inscritos.length) : 0,
                                tipo: 'moneda',
                            },
                        ],
                    },
                    {
                        nombre: 'Inscritos',
                        columnas: [
                            { header: 'Nombre', key: 'nombre', tipo: 'texto', ancho: 34 },
                            { header: 'RUT', key: 'rut', tipo: 'texto' },
                            { header: 'Email', key: 'email', tipo: 'texto', ancho: 32 },
                            { header: 'Teléfono', key: 'telefono', tipo: 'texto' },
                            { header: 'Tipo de inscripción', key: 'tipoInscripcion', tipo: 'texto' },
                            { header: 'Plan', key: 'plan', tipo: 'texto', ancho: 24 },
                            { header: 'Monto total', key: 'montoTotal', tipo: 'moneda', total: true },
                            {
                                header: 'Estado', key: 'estado', tipo: 'texto',
                                resaltar: (i) => (i.estado === 'confirmada' ? 'ok' : i.estado === 'cancelada' ? 'alerta' : 'aviso'),
                            },
                            {
                                header: 'Pago completado', key: 'pagoCompletado', tipo: 'texto',
                                resaltar: (i) => (i.pagoCompletado === 'No' ? 'alerta' : 'ok'),
                            },
                            { header: 'Fecha de inscripción', key: 'fechaInscripcion', tipo: 'fecha' },
                            { header: 'Inicio suscripción', key: 'fechaInicioSuscripcion', tipo: 'fecha' },
                            { header: 'Término suscripción', key: 'fechaTerminoSuscripcion', tipo: 'fecha' },
                        ],
                        filas: inscritos.map((ins) => ({
                            nombre: ins.usuarioNombre || ins.inscrito?.nombre,
                            rut: ins.usuarioRut || ins.inscrito?.rut,
                            email: ins.usuarioEmail || ins.inscrito?.email,
                            telefono: ins.usuarioTelefono || ins.inscrito?.telefono,
                            tipoInscripcion: ins.tipoInscripcion === 'para_familiar' ? 'Para familiar' : 'Para mí',
                            plan: ins.planSeleccionado || ins.plan?.nombre,
                            montoTotal: typeof ins.montoTotal === 'number' ? ins.montoTotal : 0,
                            estado: ins.estado,
                            pagoCompletado: textoPago(ins),
                            fechaInscripcion: ins.fechaInscripcion,
                            fechaInicioSuscripcion: ins.fechaInicioSuscripcion,
                            fechaTerminoSuscripcion: ins.fechaTerminoSuscripcion,
                        })),
                        notas: ['Datos congelados al momento del cierre del ciclo (snapshot).'],
                    },
                ],
            });
        } catch (error) {
            modal.error({
                title: 'Error al generar Excel',
                content: 'No se pudo generar el archivo. Intenta nuevamente.',
            });
        } finally {
            setDescargandoExcel(false);
        }
    };

    // Columnas de la tabla
    const columns = [
        {
            title: 'Ciclo',
            dataIndex: 'numeroCiclo',
            key: 'numeroCiclo',
            width: 80,
            render: (numeroCiclo) => (
                <Tag color="blue" className="text-center font-bold text-sm">
                    Ciclo {numeroCiclo}
                </Tag>
            ),
            sorter: (a, b) => b.numeroCiclo - a.numeroCiclo,
        },
        {
            title: 'Fecha de Cierre',
            dataIndex: 'fechaCierre',
            key: 'fechaCierre',
            width: 120,
            render: (fecha) => (
                <div className="flex items-center gap-2">
                    <CalendarOutlined className="text-gray-500" />
                    {dayjs(fecha).format('DD/MM/YYYY')}
                </div>
            ),
            sorter: (a, b) => new Date(b.fechaCierre) - new Date(a.fechaCierre),
        },
        {
            title: 'Período',
            key: 'periodo',
            width: 150,
            render: (_, record) => (
                <div className="text-xs text-gray-700">
                    <div><strong>Inicio:</strong> {dayjs(record.fechaInicio).format('DD/MM/YY')}</div>
                    <div><strong>Fin:</strong> {dayjs(record.fechaFin).format('DD/MM/YY')}</div>
                </div>
            ),
        },
        {
            title: 'Inscritos',
            dataIndex: ['estadisticas', 'totalInscritos'],
            key: 'totalInscritos',
            width: 100,
            align: 'center',
            render: (total) => (
                <div>
                    <div className="font-bold text-lg text-blue-600">{total}</div>
                    <div className="text-xs text-gray-500">Total</div>
                </div>
            ),
            sorter: (a, b) => a.estadisticas.totalInscritos - b.estadisticas.totalInscritos,
        },
        
        {
            title: 'Motivo Cierre',
            dataIndex: 'motivoCierre',
            key: 'motivoCierre',
            width: 140,
            render: (motivo) => {
                const colorMap = {
                    'fecha_fin_alcanzada': 'blue',
                    'cierre_manual': 'green',
                    'inicio_nuevo_ciclo': 'purple',
                };
                const labelMap = {
                    'fecha_fin_alcanzada': 'Fecha alcanzada',
                    'cierre_manual': 'Cierre manual',
                    'inicio_nuevo_ciclo': 'Nuevo ciclo',
                };
                return <Tag color={colorMap[motivo] || 'default'}>{labelMap[motivo] || motivo}</Tag>;
            },
        },
        {
            title: 'Acciones',
            key: 'acciones',
            width: 190,
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        icon={<EyeOutlined />}
                        type="primary"
                        size="small"
                        onClick={() => handleVerDetalle(record)}
                    >
                        Ver
                    </Button>
                    <Button
                        icon={<FileExcelOutlined />}
                        type="dashed"
                        size="small"
                        loading={descargandoExcel}
                        onClick={() => handleDescargarInscritos(record)}
                        title="Descargar inscritos como Excel"
                        style={{ color: '#0047bb', borderColor: '#0047bb' }}
                    >
                        Excel
                    </Button>
                    <Button
                        icon={<DownloadOutlined />}
                        type="default"
                        size="small"
                        onClick={() => handleDescargarCiclo(record)}
                        title="Descargar como JSON"
                    >
                        JSON
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button
                            icon={<ArrowLeftOutlined />}
                            type="text"
                            size="large"
                            onClick={() => navigate('/admin/talleres/lista')}
                            className="text-gray-600"
                        />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Historial de Ciclos</h1>
                            {taller && (
                                <p className="text-gray-600 mt-1">
                                    {taller.nombre} <span className="text-gray-400">• {taller.recinto?.nombre}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Alertas */}
                {ciclos.length === 0 && !loading && (
                    <Alert
                        message="Sin historial"
                        description="Este taller aún no tiene ciclos cerrados."
                        type="info"
                        showIcon
                        className="mb-6"
                    />
                )}

                {/* Tabla de ciclos */}
                <Card className="shadow-lg rounded-lg mb-6">
                    <Spin spinning={loading}>
                        {ciclos.length > 0 ? (
                            <Table
                                columns={columns}
                                dataSource={ciclos}
                                rowKey="_id"
                                pagination={{
                                    current: pagination.current,
                                    pageSize: pagination.pageSize,
                                    total: pagination.total,
                                    showSizeChanger: true,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} ciclos`,
                                    pageSizeOptions: ['5', '10', '20', '50'],
                                    onChange: (page, pageSize) => {
                                        setPagination({ current: page, pageSize });
                                    },
                                }}
                                scroll={{ x: 1300 }}
                                className="custom-table"
                            />
                        ) : (
                            <Empty description="No hay ciclos cerrados" />
                        )}
                    </Spin>
                </Card>

                {/* Drawer con detalle del ciclo */}
                <Drawer
                    open={drawerVisible}
                    onClose={() => setDrawerVisible(false)}
                    width={angosto ? '100%' : 880}
                    destroyOnHidden
                    closable={false}
                    title={null}
                    styles={{ header: { display: 'none' }, body: { padding: 0, background: '#f4f6f9' } }}
                >
                    {cicloSeleccionado && (() => {
                        const c = cicloSeleccionado;
                        const est = c.estadisticas || {};
                        const horarios = c.horariosSnapshot || [];
                        const inscripciones = c.inscripcionesSnapshot || [];
                        return (
                            <>
                                <CabeceraModal
                                    icon={<FiArchive size={24} />}
                                    titulo={`Ciclo ${c.numeroCiclo}`}
                                    subtitulo={c.datosTaller?.nombre}
                                    onClose={() => setDrawerVisible(false)}
                                    chips={(
                                        <>
                                            <ChipCabecera>{MOTIVOS_CIERRE[c.motivoCierre] || 'Cerrado'}</ChipCabecera>
                                            <ChipCabecera activo={false}>{c.datosTaller?.esGratuito ? 'Gratuito' : 'De pago'}</ChipCabecera>
                                        </>
                                    )}
                                    datos={[
                                        { label: 'Se cerró', value: dayjs(c.fechaCierre).format('DD/MM/YYYY'), sub: dayjs(c.fechaCierre).format('HH:mm') },
                                        { label: 'Lo cerró', value: c.cerradoPorNombre || '—', sub: c.cerradoPorRol },
                                    ]}
                                />

                                <div className="p-5 sm:p-6 space-y-5">
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                                        <Kpi icon={<FiUsers size={17} />} label="Inscritos" value={est.totalInscritos ?? 0} ok={(est.totalInscritos ?? 0) > 0} />
                                        <Kpi icon={<FiCheck size={17} />} label="Activos al cerrar" value={est.totalActivos ?? 0} ok={(est.totalActivos ?? 0) > 0} />
                                        <Kpi icon={<FiUserX size={17} />} label="Se retiraron" value={est.totalRetirados ?? 0} ok={false} alerta={(est.totalRetirados ?? 0) > 0} />
                                        <Kpi icon={<FiDollarSign size={17} />} label="Ingresos" value={`$${(est.ingresoTotal || 0).toLocaleString('es-CL')}`} ok={(est.ingresoTotal || 0) > 0} />
                                    </div>

                                    {c.notas && (
                                        <div className="p-4 rounded-2xl bg-white border border-gray-100">
                                            <p className={claseTituloSeccion}>Notas del cierre</p>
                                            <p className="text-sm text-gray-900 m-0 mt-1 whitespace-pre-wrap">{c.notas}</p>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap gap-2">
                                        <button type="button" onClick={() => handleDescargarInscritos(c)} disabled={descargandoExcel} className={claseBtnPrimario}>
                                            {descargandoExcel ? <LoadingOutlined /> : <FiDownload size={15} />} Inscritos en Excel
                                        </button>
                                        <Tooltip title="Archivo técnico con todo lo guardado del ciclo">
                                            <button type="button" onClick={() => handleDescargarCiclo(c)} className={claseBtnSecundario}>
                                                <FiFileText size={15} /> Ciclo completo (JSON)
                                            </button>
                                        </Tooltip>
                                    </div>

                                    <section className="rounded-3xl bg-white border border-gray-100 px-5 pb-5">
                                        <Tabs items={[
                                            {
                                                key: 'inscripciones',
                                                label: <Etiqueta icon={<FiUsers size={15} />} texto="Inscripciones" n={inscripciones.length} />,
                                                children: (
                                                    <div className="admin-table-card">
                                                        <Table
                                                            dataSource={inscripciones}
                                                            rowKey={(r, i) => r._id || i}
                                                            pagination={{ pageSize: 8 }}
                                                            scroll={{ x: 600 }}
                                                            locale={{ emptyText: <Vacio titulo="Sin inscripciones" texto="Este ciclo no tuvo inscritos." /> }}
                                                            columns={[
                                                                {
                                                                    title: 'Persona',
                                                                    key: 'persona',
                                                                    render: (_, r) => (
                                                                        <div className="min-w-0">
                                                                            <p className="font-bold text-gray-900 m-0 truncate">{r.usuarioNombre || r.inscrito?.nombre || '—'}</p>
                                                                            <p className="text-xs text-gray-500 m-0 truncate">
                                                                                <span className="tabular-nums">{r.usuarioRut || r.inscrito?.rut || '—'}</span>
                                                                                {(r.usuarioEmail || r.inscrito?.email) && <> · {r.usuarioEmail || r.inscrito?.email}</>}
                                                                            </p>
                                                                        </div>
                                                                    ),
                                                                },
                                                                {
                                                                    title: 'Estado',
                                                                    dataIndex: 'estado',
                                                                    key: 'estado',
                                                                    width: 130,
                                                                    render: (estado) => {
                                                                        const [tono, texto] = ESTADOS_INSCRIPCION[estado] || ['gris', estado || '—'];
                                                                        return <Chip tono={tono}>{texto}</Chip>;
                                                                    },
                                                                },
                                                                {
                                                                    title: 'Plan',
                                                                    dataIndex: 'planSeleccionado',
                                                                    key: 'planSeleccionado',
                                                                    width: 110,
                                                                    render: (plan) => plan ? <span className="capitalize"><ChipNeutro>{plan}</ChipNeutro></span> : '—',
                                                                },
                                                                {
                                                                    title: 'Pagado',
                                                                    dataIndex: 'montoTotal',
                                                                    key: 'montoTotal',
                                                                    align: 'right',
                                                                    width: 100,
                                                                    render: (monto) => <span className="font-semibold tabular-nums">${(monto || 0).toLocaleString('es-CL')}</span>,
                                                                },
                                                            ]}
                                                        />
                                                    </div>
                                                ),
                                            },
                                            {
                                                key: 'horarios',
                                                label: <Etiqueta icon={<FiClock size={15} />} texto="Horarios" n={horarios.length} />,
                                                children: horarios.length === 0
                                                    ? <Vacio titulo="Sin horarios" texto="No quedaron horarios guardados en este ciclo." />
                                                    : (
                                                        <div className="space-y-2">
                                                            {horarios.map((h, idx) => {
                                                                const bloques = Array.isArray(h.bloques) && h.bloques.length > 0
                                                                    ? h.bloques
                                                                    : [{ dias: h.dias || (h.dia ? [h.dia] : []), inicio: h.horaInicio || h.inicio, fin: h.horaFin || h.fin }];
                                                                const disp = h.cuposDisponibles ?? Math.max(0, (h.cupos || 0) - (h.cuposOcupados || 0) - (h.cuposReservados || 0));
                                                                return (
                                                                    <div key={idx} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50 border border-gray-100">
                                                                        <div className="space-y-1 min-w-0">
                                                                            {bloques.map((b, i) => (
                                                                                <div key={i} className="flex items-center gap-1.5 flex-wrap">
                                                                                    {(b.dias || []).map(d => <Chip key={d} tono="azul" className="capitalize">{String(d).slice(0, 3)}</Chip>)}
                                                                                    <span className="text-sm font-semibold text-gray-700 tabular-nums">{b.inicio || '—'}–{b.fin || '—'}</span>
                                                                                </div>
                                                                            ))}
                                                                            {h.nombre && <p className="text-xs text-gray-500 m-0">{h.nombre}</p>}
                                                                        </div>
                                                                        <p className="text-xs text-gray-500 m-0 flex-shrink-0 tabular-nums">
                                                                            <strong className="text-gray-900">{h.cuposOcupados || 0}</strong> inscritos · {disp}/{h.cupos || 0} libres
                                                                        </p>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ),
                                            },
                                            {
                                                key: 'taller',
                                                label: <Etiqueta icon={<FiInfo size={15} />} texto="Datos del taller" />,
                                                children: (
                                                    <div>
                                                        {[
                                                            ['Nombre', c.datosTaller?.nombre],
                                                            ['Tipo de actividad', c.datosTaller?.tipoActividad],
                                                            ['Descripción', c.datosTaller?.descripcion],
                                                            ['Precio', c.datosTaller?.esGratuito ? 'Gratuito' : 'De pago'],
                                                        ].map(([label, valor]) => (
                                                            <div key={label} className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-4 py-3 border-b border-gray-100 last:border-0">
                                                                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 m-0">{label}</p>
                                                                <p className="text-sm text-gray-900 m-0">{valor || '—'}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ),
                                            },
                                        ]} />
                                    </section>
                                </div>
                            </>
                        );
                    })()}
                </Drawer>
            </div>

            <style jsx>{`
                .custom-table :global(.ant-table-thead > tr > th) {
                    background-color: #f8fafc;
                    color: #1f2937;
                    font-weight: 600;
                    border-bottom: 2px solid #e5e7eb;
                }
                .custom-table :global(.ant-table-tbody > tr:hover > td) {
                    background-color: #eff6ff;
                }
            `}</style>
        </div>
    );
};

export default TalleresHistorialCiclos;
